import type {
    CameraDevice,
    Html5QrcodeCameraScanConfig,
    QrcodeErrorCallback,
    QrcodeSuccessCallback,
} from 'html5-qrcode';

export interface CameraStartOption {
    constraints: MediaTrackConstraints;
    label: string;
}

export interface ScannerController {
    start(
        cameraIdOrConfig: MediaTrackConstraints,
        configuration: Html5QrcodeCameraScanConfig | undefined,
        qrCodeSuccessCallback: QrcodeSuccessCallback | undefined,
        qrCodeErrorCallback: QrcodeErrorCallback | undefined
    ): Promise<null>;
    stop(): Promise<void>;
    clear(): void;
    getRunningTrackSettings(): MediaTrackSettings;
}

interface StartScannerWithFallbackOptions {
    cameraOptions: CameraStartOption[];
    scanConfig: Html5QrcodeCameraScanConfig;
    createScanner: () => ScannerController;
    onDecode: QrcodeSuccessCallback;
    onScannerChange?: (scanner: ScannerController | null) => void;
    shouldContinue?: () => boolean;
}

const REAR_CAMERA_LABEL_PATTERN =
    /\b(back|rear|environment|world|main|telephoto|wide)\b|facing\s*back/i;
const FRONT_CAMERA_LABEL_PATTERN = /\b(front|user|selfie)\b|facing\s*front/i;

function getCameraPreferenceScore(device: CameraDevice, index: number) {
    const label = device.label.toLowerCase();

    if (FRONT_CAMERA_LABEL_PATTERN.test(label)) {
        return -100;
    }

    if (REAR_CAMERA_LABEL_PATTERN.test(label)) {
        return 100 + index;
    }

    return 0;
}

function getPreferredRearCamera(devices: CameraDevice[]) {
    const [bestDevice, bestScore] = devices.reduce<
        [CameraDevice | null, number]
    >(
        ([currentDevice, currentScore], device, index) => {
            const score = getCameraPreferenceScore(device, index);
            return score > currentScore
                ? [device, score]
                : [currentDevice, currentScore];
        },
        [null, 0]
    );

    return bestScore > 0 ? bestDevice : null;
}

function createDeviceOption(device: CameraDevice): CameraStartOption {
    return {
        constraints: { deviceId: { exact: device.id } },
        label: device.label || device.id,
    };
}

export function getCameraStartOptions(devices: CameraDevice[]) {
    const seenDeviceIds = new Set<string>();
    const deviceOptions = [
        getPreferredRearCamera(devices),
        devices[devices.length - 1],
        ...devices.slice().reverse(),
    ].reduce<CameraStartOption[]>((options, device) => {
        if (!device || seenDeviceIds.has(device.id)) {
            return options;
        }

        seenDeviceIds.add(device.id);
        return [...options, createDeviceOption(device)];
    }, []);

    return [
        {
            constraints: { facingMode: { exact: 'environment' } },
            label: 'environment camera',
        },
        {
            constraints: { facingMode: 'environment' },
            label: 'preferred environment camera',
        },
        ...deviceOptions,
    ];
}

function isUserFacingCamera(scanner: ScannerController) {
    try {
        return scanner.getRunningTrackSettings().facingMode === 'user';
    } catch (err) {
        console.warn('[ImeiScanner] unable to read camera settings:', err);
        return false;
    }
}

async function stopStartedScanner(scanner: ScannerController) {
    await scanner.stop();
    scanner.clear();
}

export async function startScannerWithFallback({
    cameraOptions,
    scanConfig,
    createScanner,
    onDecode,
    onScannerChange,
    shouldContinue = () => true,
}: StartScannerWithFallbackOptions): Promise<ScannerController | null> {
    const tryStartCamera = async (
        optionIndex: number,
        lastError: unknown = null
    ): Promise<ScannerController | null> => {
        if (!shouldContinue()) return null;

        const cameraOption = cameraOptions[optionIndex];
        if (!cameraOption) {
            throw lastError instanceof Error
                ? lastError
                : new Error('Failed to start camera');
        }

        const scanner = createScanner();
        onScannerChange?.(scanner);

        try {
            await scanner.start(
                cameraOption.constraints,
                scanConfig,
                onDecode,
                undefined
            );

            if (!shouldContinue()) {
                onScannerChange?.(null);
                await stopStartedScanner(scanner);
                return null;
            }

            if (
                isUserFacingCamera(scanner) &&
                optionIndex < cameraOptions.length - 1
            ) {
                onScannerChange?.(null);
                await stopStartedScanner(scanner);
                return tryStartCamera(
                    optionIndex + 1,
                    new Error(`${cameraOption.label} started the front camera`)
                );
            }

            return scanner;
        } catch (err: unknown) {
            onScannerChange?.(null);

            try {
                scanner.clear();
            } catch (clearError) {
                console.warn(
                    `[ImeiScanner] clear error after failing to start ${cameraOption.label}:`,
                    clearError
                );
            }

            return tryStartCamera(optionIndex + 1, err);
        }
    };

    return tryStartCamera(0);
}
